import type { ComponentProps, RefObject } from 'react';
import React, { useId } from 'react';
import styled from '@emotion/styled';
import Downshift from 'downshift';
import {
  backgroundColor,
  border,
  display,
  flex,
  fontSize,
  height,
  left,
  position,
  space,
  themeGet,
  top,
  width,
} from 'styled-system';

import { SEARCH_BAR_Z_INDEX } from '../../constants/z-index';
import { isIOSDevice } from '../../DeviceDetector';
import { useMatchesViewport } from '../../hooks/media';
import { useLocalization } from '../../providers/LocaleProvider';
import { outline, pointerEvents, themeTop } from '../../theme/system-utilities';
import type { SearchParams } from '../../types/search';
import SearchFormContainer from '../AutocompleteInputV2/SearchFormContainer';
import MovableButton from '../Button/MovableButton';
import { Flex } from '../Flex';
import Icon from '../Icon';
import Transition from '../Transition';
import { BodyRegularBR1 } from '../Typography';
import VisuallyHidden from '../VisuallyHidden';
import ClearButton from './ClearButton';
import { useSearch } from './hooks';
import type { SuggestionsConfig } from './hooks/suggestions/types';
import SearchIcon from './SearchIcon';
import SearchInputBorder from './SearchInputBorder';
import SearchInputButton from './SearchInputButton';
import SuggestionsBox from './SuggestionsBox';
import { getSuggestionName } from './utils/search';
import { trackSearchBack, trackSearchOpen, trackSearchSubmit } from './utils/tracking';

interface SearchProps extends Partial<SuggestionsConfig> {
  showOnlySearchIcon?: boolean;
  params: SearchParams;
  withRecentSearches?: boolean;
  withProductSuggestions?: boolean;
  shopName?: string;
  filterQuery?: string;
  iconVariant?: 'primary' | 'secondary';
  withAnimation?: boolean;
  overlayStacking?: number;
  // This prop will control the size of the search icon.
  // This can be used with 'showOnlySearchIcon' prop to have the search icon in small or large size.
  minimalIcon?: boolean;
  withInputFilled?: boolean;
  parentRef?: RefObject<HTMLElement>;
}

const TRANSITION_MS = 360;
const BACK_BTN_HIDE_LEFT = '-40px';
const SEARCH_INPUT_TARGET_HEIGHT = 44;
const SEARCH_INPUT_VISUAL_HEIGHT = 36;

const UnstyledFormWrapper = styled.div<any>`
  ${space};
  ${display};
`;

const FormWrapper = (props: ComponentProps<typeof UnstyledFormWrapper>) => (
  <UnstyledFormWrapper pt={['sp_12', 'sp_0']} {...props} />
);

const UnstyledForm = styled(Flex)<any>`
  ${position};
  ${height};
`;

const Form = (props: ComponentProps<typeof UnstyledForm>) => (
  <UnstyledForm as="form" position="relative" data-cnstrc-search-form="" {...props} />
);

const UnstyledSearchInputContainer = styled.div<any>`
  ${position};
  ${display};
  ${flex};
  ${space};
  ${height};

  &:hover,
  &:focus {
    span:last-child {
      background-color: ${themeGet('colors.sys.primary.background.hover')};
    }
  }
`;

const SearchInputContainer = (props: ComponentProps<typeof UnstyledSearchInputContainer>) => (
  <UnstyledSearchInputContainer position="relative" display="flex" flex="1 1 auto" {...props} />
);

const UnstyledSearchInputMobileOverlay = styled.div<any>`
  ${display};
  ${position};
  ${width};
  ${height};
  ${top};
  ${left};
`;

const SearchInputMobileOverlay = (
  props: ComponentProps<typeof UnstyledSearchInputMobileOverlay>
) => (
  <UnstyledSearchInputMobileOverlay
    position="absolute"
    width={1}
    height="100%"
    top={0}
    left={0}
    display={[null, 'none']}
    {...props}
  />
);

const UnstyledFakeInputMobile = styled(VisuallyHidden)<any>`
  ${position};
  ${fontSize};
  ${themeTop};
`;

const FakeInputMobile = (props: ComponentProps<typeof UnstyledFakeInputMobile>) => (
  <UnstyledFakeInputMobile
    as="input"
    position="fixed"
    fontSize="fs_32"
    themeTop="sp_0"
    {...props}
  />
);

const UnstyledSearchInput = styled(BodyRegularBR1)<any>`
  ${pointerEvents};
  ${backgroundColor};
  ${width};
  ${border};
  ${outline};

  &::placeholder {
    color: ${themeGet('colors.sys.neutral.text.default')};
  }

  &[type='search'] {
    -webkit-appearance: none;

    &:invalid {
      box-shadow: none !important;
    }

    &::-ms-clear {
      display: none;
    }

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      -webkit-appearance: none;
    }
  }
`;

const SearchInput = (props: ComponentProps<typeof UnstyledSearchInput>) => (
  <UnstyledSearchInput
    as="input"
    backgroundColor="transparent"
    border="none"
    m="sp_0"
    width={1}
    outline="none"
    textOverflow="ellipsis"
    pointerEvents={['none', 'auto']}
    color="sys.neutral.text.strong"
    pl="sp_16"
    pr="sp_0"
    py="sp_10"
    data-cnstrc-search-input=""
    {...props}
  />
);

const SearchFilterQuery = ({ filterQuery }: Pick<SearchProps, 'filterQuery'>) => {
  if (!filterQuery) return null;
  const params = filterQuery.split('&').reduce((acc, param) => {
    const [key, value] = param.split('=');
    if (key && value) {
      acc[key] = value;
    }

    return acc;
  }, {});

  return (
    <>
      {Object.keys(params).map(key => (
        <input type="hidden" name={key} value={params[key]} key={key} />
      ))}
    </>
  );
};

// TODO: Refactor this component and enable linter rules
/* eslint-disable max-lines, max-lines-per-function */
const Search = ({
  searchDebounce = 300,
  suggestionsLimit = 10,
  searchThreshold = 1,
  productsLimit = 6,
  showOnlySearchIcon,
  params,
  withRecentSearches = true,
  withProductSuggestions = true,
  shopName,
  filterQuery,
  iconVariant,
  withAnimation = false,
  overlayStacking,
  minimalIcon = false,
  withInputFilled = true,
  parentRef,
}: SearchProps) => {
  const { t } = useLocalization();
  const searchInputId = useId();
  const {
    matches: { matchesTablet },
  } = useMatchesViewport();
  const isMobile = !matchesTablet;
  const { input, suggestions, recentSearches } = useSearch({
    matchesTablet,
    config: {
      searchDebounce,
      suggestionsLimit,
      productsLimit,
      searchThreshold,
    },
    params,
    withRecentSearches,
    withProductSuggestions,
    shopName,
    filterQuery,
    overlayStacking,
    withInputFilled,
    parentRef,
  });

  return (
    <Transition
      in={input.flags.isLocked}
      timeout={TRANSITION_MS}
      onEnter={() => {
        // After we set the top in `lockSearch` callback remove
        // the top value from styles to start "entering" animation
        input.refs.container.current.style.top = null;
        trackSearchOpen(shopName);
      }}
      onEntered={() => {
        // After the animation ended, move focus to the real input
        // field (iOS keyboard is already on screen, everything is fine)
        input.refs.input.current.focus();
      }}
      onExited={() => {
        // After exit animation, clean inline styles
        input.refs.container.current.style.willChange = null;
        input.refs.container.current.style.top = null;
      }}
      nodeRef={input.refs.container as unknown as React.RefObject<null | undefined>}
    >
      {lockedTransitionState => (
        <Downshift
          id={searchInputId}
          inputValue={input.value}
          itemToString={getSuggestionName}
          isOpen={suggestions.flags.isOpen}
          onSelect={suggestions.handlers.handleSelect}
        >
          {/* eslint-disable-next-line complexity */}
          {({
            getRootProps,
            getInputProps,
            getItemProps,
            getMenuProps,
            getLabelProps,
            highlightedIndex,
            isOpen,
          }) => {
            const rootProps = getRootProps(
              {
                onFocus: input.handlers.handleFocus,
                onBlur: input.handlers.handleBlur,
                // Form container animation is half the time of the whole
                // animation cycle. We sync a lot of things with the same timeout
                // allowing to show the form first, before animating buttons
                timeout: TRANSITION_MS / 2,
                transitionState: lockedTransitionState,
                ref: input.refs.container,
                'data-testid': 'search-container',
              } as any,
              // Downshift doesn't recognize that the ref is passed correctly
              // https://github.com/downshift-js/downshift#getrootprops
              { suppressRefError: true }
            );
            const formProps = {
              name: 'search',
              role: 'search',
              action: input.actionPath,
              onClick(event) {
                if (!isMobile) {
                  input.refs.input.current.focus();
                }
                // Prevent default click tracking on the form. This
                // will allow to push event only when it is coming from
                // submit event
                event.nativeEvent.trackingPreventDefault = true;
              },
              locked: input.flags.isLocked,
              height: SEARCH_INPUT_TARGET_HEIGHT,
              alignItems: 'center',
              onSubmit: () => trackSearchSubmit(input.value, shopName),
            };

            const menuProps = getMenuProps({
              open: isOpen,
              refKey: 'containerRef',
            });

            const inputProps = getInputProps({
              name: 'query',
              type: 'sys.neutral.text.strong',
              required: true,
              placeholder: input.placeholder,
              onChange: input.handlers.handleValueChange,
              ref: input.refs.input,
              'data-testid': 'search-bar-input',
              pointerEvents: input.flags.isLocked ? 'auto' : undefined,
            });

            const showSearchIconInsteadForm =
              !input.flags.isLocked && !!showOnlySearchIcon && lockedTransitionState === 'exited';

            const hasExtraPadding =
              isIOSDevice() && input.flags.isKeyboardOpen && isOpen && isMobile;

            return (
              <>
                <SearchFormContainer {...rootProps}>
                  <FormWrapper
                    // Adjust paddings when locked
                    px={lockedTransitionState === 'exited' ? 0 : 3}
                    pb={lockedTransitionState === 'exited' ? 0 : '12px'}
                    data-testid="form-wrapper"
                    {...(showSearchIconInsteadForm ? { display: 'none' } : {})}
                  >
                    <Form {...formProps}>
                      <MovableButton
                        data-testid="search-back-button"
                        onClick={() => {
                          input.handlers.handleBackButtonClick();
                          trackSearchBack(input.value, shopName);
                        }}
                        timeout={TRANSITION_MS}
                        // Back button will appear with delay after SearchFormContainer
                        // finished animating, but will disappear first
                        delay={TRANSITION_MS / 2}
                        hidden={!input.flags.isLocked}
                        aria-label={t('h24_back')}
                        aria-hidden={!input.flags.isLocked}
                        tabIndex={!input.flags.isLocked ? -1 : 0}
                        hideToLeft={BACK_BTN_HIDE_LEFT}
                        mr={2}
                      >
                        <Icon name="arrowLeft" dataTestId="back-button-icon" size={32} />
                      </MovableButton>

                      <VisuallyHidden {...getLabelProps({ as: 'label' })}>
                        {t('h24_search_bar_placeholder')}
                      </VisuallyHidden>
                      <SearchInputBorder
                        data-testid="search-input-border"
                        locked={input.flags.isLocked}
                        focused={input.flags.focused}
                        timeout={TRANSITION_MS / 2}
                        {...(input.flags.isBodyOverlayVisible
                          ? {
                              zIndex: `${SEARCH_BAR_Z_INDEX + 1}`,
                            }
                          : {})}
                        onClick={() => trackSearchOpen(shopName)}
                      >
                        <SearchInputContainer height={SEARCH_INPUT_VISUAL_HEIGHT}>
                          {!input.flags.isLocked && (
                            // This overlay will only be visible on mobile
                            // and only when the form is not "locked". We need
                            // it to work around iOS bugs with screen auto scroll
                            // when focusing on the inputs that are not completely
                            // in the upper part of the page.
                            //
                            // This overlay will trigger focus on the FakeInputMobile
                            // that is always on the top of the page, and this fake
                            // input will start the lock screen process
                            <SearchInputMobileOverlay
                              data-testid="search-overlay"
                              onClick={() => {
                                // Triggering focus immediately on click allows
                                // for iOS keyboard to appear on the screen
                                input.refs.fakeInput.current.focus();
                              }}
                              onFocus={() => {
                                input.refs.fakeInput.current.focus();
                              }}
                              tabIndex={0}
                            />
                          )}
                          <SearchInput {...inputProps} />
                          <SearchFilterQuery filterQuery={filterQuery} />
                          <ClearButton input={input} shopName={shopName} />
                          <SearchInputButton iconVariant={iconVariant} />
                        </SearchInputContainer>
                      </SearchInputBorder>
                    </Form>
                  </FormWrapper>
                  {showSearchIconInsteadForm && (
                    <SearchIcon
                      withAnimation={withAnimation}
                      minimalIcon={minimalIcon}
                      onClick={() => input.refs.fakeInput.current?.focus()}
                    />
                  )}
                  <Transition
                    in={isOpen}
                    timeout={TRANSITION_MS / 2}
                    nodeRef={menuProps.containerRef}
                  >
                    <SuggestionsBox
                      menuProps={menuProps}
                      suggestions={suggestions.suggestions}
                      recentSearches={recentSearches}
                      productSuggestions={suggestions.productSuggestions}
                      listItems={suggestions.listItems}
                      open={isOpen}
                      timeout={TRANSITION_MS / 2}
                      inputValue={input.value}
                      highlightedIndex={highlightedIndex}
                      getItemProps={getItemProps}
                      hasExtraPadding={hasExtraPadding}
                      isBodyOverlayVisible={input.flags.isBodyOverlayVisible}
                      exitSuggestions={suggestions.handlers.exit}
                      inputContainerRef={input.refs.container}
                      shopName={shopName}
                    />
                  </Transition>
                </SearchFormContainer>
                {/* Placeholder element, that shows up when search container becomes fixed */}
                <div
                  aria-hidden="true"
                  ref={input.refs.placeholder}
                  style={{
                    display: lockedTransitionState === 'exited' ? 'none' : 'block',
                    // Search height 40px + padding top 16px, we can do some fancy calculations for
                    // this but eh...
                    height: '56px',
                  }}
                />
                {/*
                 * We need this fake input for iOS to prevent screen jumping bugs when
                 * focusing in the elements that are slightly off the upper part of the
                 * screen. We cover real search input with an overlay and immediately
                 * trigger focus on this fake one. As the fake input is always stuck on
                 * the top of the page, iOS is tricked into not doing any weird scrolling.
                 * As soon as focus happened, we trigger locked state and move focus back
                 * to the real search input.
                 */}
                {isMobile && (
                  <label htmlFor="search-box-input-focus-helper">
                    <FakeInputMobile
                      ref={input.refs.fakeInput}
                      onFocus={input.handlers.lockSearch}
                      id="search-box-input-focus-helper"
                      aria-label={inputProps.placeholder}
                      data-testid="search-box-input-focus-helper"
                    />
                  </label>
                )}
              </>
            );
          }}
        </Downshift>
      )}
    </Transition>
  );
};

export default Search;
